import React, { useCallback } from 'react';
import { useAudios } from '@dyl/systems/AudiosManager';
import ObjectPicker from '@dyl/components/ObjectPicker';
import { property } from 'lodash';
import AudioMiniPreview from '../AudioMiniPreview';
import OverlayBox from '../OverlayBox';
import useAssetUpload from '@dyl/hooks/useAssetUpload';
import { AudioAssetMimeTypes } from '@dyl/constants/AssetConstants';
import quickDateFormat from '@dyl/utils/quickDateFormat';

/** @type {import('@mui/x-data-grid').GridColDef[]} */
const Columns = [
    { headerName: 'Name', field: 'audio_id', flex: 1,
        renderCell({ value, row }) {
            return (
                <>
                    <AudioMiniPreview audioId={row.audio_id} />
                    {value}
                </>
            );
        },
    },
    { headerName: 'Transcript', field: 'transcript', flex: 1 },
    { headerName: 'Last updated', field: 'updated_at', flex: 1,
        valueFormatter({ value }) {
            return quickDateFormat(value);
        },
    },
];

const getId = property('audio_id');
const getName = property('audio_id');

/** @typedef {ReturnType<useAudios>['audios'] extends (infer R)[] ? R : never} Audio */

/**
 * @param {import('@dyl/components/ObjectPicker').ObjectPickerProps<Audio>} props
 */
export default function AudioPicker({ value, error, ...other }) {
    const { audios, audiosStatus, refreshAudios } = useAudios();

    const baseUpload = useAssetUpload(audios);
    const uploadFile = useCallback(file => baseUpload('sounds', file), [baseUpload]);

    return (
        <OverlayBox isLoading={audiosStatus !== 'complete'} hideLabel>
            <ObjectPicker
                columns={Columns}
                data={audios}
                value={value}
                isLoading={audiosStatus !== 'complete'}
                getId={getId}
                getDisplayName={getName}
                searchColumn='audio_id'
                FieldProps={{
                    error: !!error,
                    InputProps: {
                        endAdornment: <AudioMiniPreview audioId={value} />,
                    },
                }}
                refresh={refreshAudios}
                allowUpload
                uploadAccept={AudioAssetMimeTypes.join()}
                onUploadConfirm={uploadFile}
                PreviewUploadComponent={UploaderPreview}
                {...other}
            />
        </OverlayBox>
    );
}

function UploaderPreview({ file }) {
    return <AudioMiniPreview blob={file} />;
};